import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import { useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";

import Navbar from "../../components/site/Navbar";
import Footer from "../../components/site/Footer";
import { useCategories } from "../../hooks/useCategories";
import { useSections } from "../../hooks/useSections";
import { useGarments } from "../../hooks/useGarments";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/firebase";

function formatPrice(value) {
  const amount = Number(
    value?.startingPrice ??
    value?.basePrice ??
    value?.fixedPrice ??
    0
  );

  if (!Number.isFinite(amount) || amount <= 0) {
    return "Price on request";
  }

  return `?${amount.toLocaleString("en-IN")}`;
}

function LoadingGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {[0, 1, 2, 3, 4, 5].map((item) => (
        <div
          key={item}
          className="card-soft overflow-hidden animate-pulse"
        >
          <div className="aspect-[4/3] bg-[#FFE7D2]" />
          <div className="p-5">
            <div className="h-7 rounded bg-[#FFE7D2] w-2/3" />
            <div className="h-4 rounded bg-[#FFF0E3] mt-3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SectionPage() {
  const { categoryId, sectionId, subsectionId } = useParams();
  const navigate = useNavigate();
  const [subsections, setSubsections] = useState([]);
  const [subsectionsLoading, setSubsectionsLoading] = useState(true);
  const [subsectionsError, setSubsectionsError] = useState("");

  const {
    categories,
    categoriesLoading,
    categoriesError,
  } = useCategories();

  const {
    sections,
    sectionsLoading,
    sectionsError,
  } = useSections(categoryId);

  const {
    garments,
    garmentsLoading,
    garmentsError,
  } = useGarments(sectionId);

  const category = useMemo(
    () => categories.find((item) => item.id === categoryId),
    [categories, categoryId]
  );

  const section = useMemo(
    () => sections.find((item) => item.id === sectionId),
    [sections, sectionId]
  );

  useEffect(() => {
    if (String(section?.name || "").trim().toLowerCase() === "alterations") {
      navigate("/services/alterations", { replace: true });
    }
  }, [navigate, section]);

  useEffect(() => onSnapshot(collection(db, "stitching_subsections"), snapshot => {
    setSubsections(snapshot.docs.map(document => {
      const data = document.data();
      return { id: document.id, ...data, imageUrl: data.websiteImageUrl || data.imageUrl || data.image || "" };
    })
      .filter(item => item.isActive !== false && item.sectionId === sectionId)
      .sort((a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0)));
    setSubsectionsLoading(false);
    setSubsectionsError("");
  }, () => {
    setSubsections([]);
    setSubsectionsLoading(false);
    setSubsectionsError("Unable to load subsections.");
  }), [sectionId]);

  const selectedSubsection = useMemo(
    () => subsections.find(item => item.id === subsectionId),
    [subsections, subsectionId]
  );
  const visibleGarments = useMemo(
    () => subsectionId ? garments.filter(item => String(item.subsectionId || "") === subsectionId) : garments,
    [garments, subsectionId]
  );

  const loading =
    categoriesLoading ||
    sectionsLoading ||
    garmentsLoading || subsectionsLoading;

  const error =
    categoriesError ||
    sectionsError ||
    garmentsError || subsectionsError;

  return (
    <div className="min-h-screen bg-[#FFF8F2]">
      <Navbar onBook={() => navigate("/#services")} />

      <main className="pt-28 pb-24">
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <button
            type="button"
            onClick={() => navigate(`/category/${categoryId}`)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#F46524] hover:opacity-80"
          >
            <ArrowLeft size={16} />
            Back to sections
          </button>

          <div className="mt-8 max-w-3xl">
            <div className="font-label text-[#F46524] mb-4">
              {category?.name || "StichB"}
            </div>

            <h1 className="font-display text-4xl md:text-6xl tracking-tight text-[#2B2B2B]">
              {selectedSubsection?.name || section?.name || "Section"}
            </h1>

            <p className="text-[#5b5a56] mt-5 text-lg leading-relaxed">
              {selectedSubsection?.description || section?.description ||
                "Choose an item to continue with your booking."}
            </p>
          </div>

          <div className="mt-14">
            {loading ? (
              <LoadingGrid />
            ) : error ? (
              <div className="card-soft p-8 text-center">
                <h2 className="font-display text-2xl">
                  Items could not be loaded
                </h2>
                <p className="text-[#5b5a56] mt-2">
                  Please refresh the page and try again.
                </p>
              </div>
            ) : !subsectionId && subsections.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {subsections.map((item) => {
                  const image = item.websiteImageUrl || item.imageUrl || item.image || "";
                  return (
                    <button key={item.id} type="button" onClick={() => navigate(`/category/${categoryId}/section/${sectionId}/subsection/${item.id}`)} className="group card-soft text-left overflow-hidden lift">
                      <div className="aspect-[4/3] overflow-hidden bg-[#FFF3E8]">
                        {image ? <img src={image} alt={item.name} className="w-full h-full object-contain object-center transition-opacity duration-500" /> : <div className="w-full h-full flex items-center justify-center text-[#F46524] font-semibold">{item.name}</div>}
                      </div>
                      <div className="p-5 flex items-start justify-between gap-3"><div><h3 className="font-display text-xl">{item.name}</h3>{item.description ? <p className="text-sm text-[#5b5a56] mt-2 line-clamp-2">{item.description}</p> : null}</div><span className="w-9 h-9 rounded-full bg-[#FFEDDD] text-[#F46524] flex items-center justify-center shrink-0"><ArrowRight size={16}/></span></div>
                    </button>
                  );
                })}
              </div>
            ) : visibleGarments.length === 0 ? (
              <div className="card-soft p-8 text-center">
                <h2 className="font-display text-2xl">
                  No items available yet
                </h2>
                <p className="text-[#5b5a56] mt-2">
                  Items added from the admin panel will appear here.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {visibleGarments.map((garment) => {
                  const image =
                    garment.mainImage ||
                    garment.image ||
                    garment.imageUrl ||
                    "";

                  return (
                    <button
                      key={garment.id}
                      type="button"
                      onClick={() =>
                        navigate(
                          `/category/${categoryId}/section/${sectionId}/item/${garment.id}`
                        )
                      }
                      className="group card-soft text-left overflow-hidden lift"
                    >
                      <div className="aspect-[4/3] overflow-hidden bg-[#FFF3E8]">
                        {image ? (
                          <img
                            src={image}
                            alt={garment.name}
                            loading="lazy"
                            className="w-full h-full object-contain object-center transition-opacity duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center px-4 text-center text-[#F46524] font-semibold">
                            {garment.name}
                          </div>
                        )}
                      </div>

                      <div className="p-5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="font-display text-xl">
                              {garment.name}
                            </h3>

                            {garment.description ? (
                              <p className="text-sm text-[#5b5a56] mt-2 line-clamp-2">
                                {garment.description}
                              </p>
                            ) : null}

                            <div className="text-sm font-semibold text-[#F46524] mt-3">
                              {formatPrice(garment)}
                            </div>
                          </div>

                          <span className="w-9 h-9 rounded-full bg-[#FFEDDD] text-[#F46524] flex items-center justify-center shrink-0 group-hover:bg-[#F46524] group-hover:text-white transition-colors">
                            <ArrowRight size={16} />
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

